import { useEffect, useMemo } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ModalHost';
import { useToast } from '@/components/ToastHost';
import { milestonesApi } from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import { mapApiErrorToFormErrors } from '@/utils/formErrors';
import { useDirtyFormReloadGuard } from '@/utils/useDirtyFormReloadGuard';
import type { Agreement, Milestone } from '@/types/domain';

interface Props {
  open: boolean;
  onClose: () => void;
  agreement: Agreement;
  milestone: Milestone | null;
}

interface FormShape {
  milestone_name: string;
  expected_date: string;
  amount: string;
}

const KNOWN: string[] = ['milestone_name', 'expected_date', 'amount'];

export function MilestoneEditor({ open, onClose, agreement, milestone }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = !!milestone;
  const defaults = useMemo<FormShape>(
    () =>
      milestone
        ? {
            milestone_name: milestone.milestone_name,
            expected_date: milestone.expected_date,
            amount: milestone.amount,
          }
        : { milestone_name: '', expected_date: '', amount: '' },
    [milestone],
  );

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting, dirtyFields, isDirty },
  } = useForm<FormShape>({ defaultValues: defaults });
  useEffect(() => {
    if (open) reset(defaults);
  }, [open, defaults, reset]);

  // Browser reload-warning safety net per G-FE-1.
  useDirtyFormReloadGuard(open && isDirty);

  const createM = useMutation({
    mutationFn: (body: FormShape) =>
      milestonesApi.create(agreement.id, {
        milestone_name: body.milestone_name.trim(),
        expected_date: body.expected_date,
        amount: body.amount,
      }),
  });
  const patchM = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<FormShape> }) =>
      milestonesApi.update(id, {
        ...(body.milestone_name !== undefined
          ? { milestone_name: body.milestone_name.trim() }
          : {}),
        ...(body.expected_date !== undefined
          ? { expected_date: body.expected_date }
          : {}),
        ...(body.amount !== undefined ? { amount: body.amount } : {}),
      }),
  });

  const onSubmit: SubmitHandler<FormShape> = async (values) => {
    try {
      if (isEdit && milestone) {
        const body: Partial<FormShape> = {};
        if (dirtyFields.milestone_name) body.milestone_name = values.milestone_name;
        if (dirtyFields.expected_date) body.expected_date = values.expected_date;
        if (dirtyFields.amount) body.amount = values.amount;
        if (Object.keys(body).length === 0) {
          onClose();
          return;
        }
        await patchM.mutateAsync({ id: milestone.id, body });
        toast.show({ severity: 'success', message: 'Milestone saved.' });
      } else {
        await createM.mutateAsync(values);
        toast.show({ severity: 'success', message: 'Milestone added.' });
      }
      void qc.invalidateQueries({
        queryKey: qk.agreementMilestones(agreement.id),
      });
      onClose();
    } catch (err) {
      const mapped = mapApiErrorToFormErrors(err, KNOWN);
      for (const [field, message] of Object.entries(mapped.fieldErrors)) {
        setError(field as keyof FormShape, { type: 'server', message });
      }
      if (mapped.formMessage)
        toast.show({ severity: 'error', message: mapped.formMessage });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit milestone' : 'Add milestone'}
      role="dialog"
      canClose={() => !isDirty || window.confirm('Discard unsaved changes?')}
    >
      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <div className="form-field">
          <label htmlFor="me-name">
            Milestone name<span className="form-required">*</span>
          </label>
          <input
            id="me-name"
            disabled={isSubmitting}
            {...register('milestone_name', {
              required: 'Name is required.',
              validate: (v) => v.trim().length > 0 || 'Name is required.',
            })}
          />
          {errors.milestone_name ? (
            <span className="form-error">{errors.milestone_name.message}</span>
          ) : null}
        </div>
        <div className="form-field">
          <label htmlFor="me-date">
            Expected date<span className="form-required">*</span>
          </label>
          <input
            id="me-date"
            type="date"
            disabled={isSubmitting}
            {...register('expected_date', { required: 'Date is required.' })}
          />
          {errors.expected_date ? (
            <span className="form-error">{errors.expected_date.message}</span>
          ) : null}
        </div>
        <div className="form-field">
          <label htmlFor="me-amount">
            Amount (USD)<span className="form-required">*</span>
          </label>
          <input
            id="me-amount"
            inputMode="decimal"
            disabled={isSubmitting}
            {...register('amount', {
              required: 'Amount is required.',
              pattern: {
                value: /^\d+(?:\.\d{1,2})?$/,
                message: 'Enter dollars and cents.',
              },
              validate: (v) => Number(v) > 0 || 'Amount must be greater than 0.',
            })}
          />
          {errors.amount ? (
            <span className="form-error">{errors.amount.message}</span>
          ) : null}
        </div>
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving…' : isEdit ? 'Save' : 'Add milestone'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
